// ─────────────────────────────────────────────────────────────────────────────
//  askXmlExportOptions
//
//  The "Export XML For…" prompt used by both Claims Report screens (standard
//  and Premium). Two decisions, asked together so neither is skipped:
//
//    1. Facility type — Clinic or Hospital. Changes <typeOfService>.
//    2. Laboratory investigations — Include or Exclude.
//
//  Same rule as the customised Claims Menu export (Premium/ClaimsMenu.js,
//  card 4): a CLINIC never carries investigations, so its lab choice is locked
//  to "Exclude" and says why. A HOSPITAL chooses; the default is Include, as
//  there, so nothing changes for someone who clicks straight through.
//
//  Resolves to { facilityType, includeInvestigations } or null on cancel.
// ─────────────────────────────────────────────────────────────────────────────

import Swal from 'sweetalert2';

const optionCss = `
  .xo-group { text-align:left; margin:14px 0 0; }
  .xo-legend { font-size:11px; font-weight:800; letter-spacing:.8px; text-transform:uppercase;
               color:#64748b; margin:0 0 6px; display:flex; justify-content:space-between; gap:8px; }
  .xo-legend span:last-child { font-weight:600; letter-spacing:0; text-transform:none; color:#94a3b8; }
  .xo-row { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
  .xo-opt { display:grid; gap:2px; padding:10px 12px; border-radius:10px; border:1.5px solid #e2e8f0;
            background:#fff; cursor:pointer; text-align:left; font-family:inherit; transition:all .15s; }
  .xo-opt:hover:not([disabled]) { border-color:#94a3b8; }
  .xo-opt b { font-size:14px; color:#0f172a; }
  .xo-opt small { font-size:11.5px; color:#64748b; line-height:1.4; }
  .xo-opt.on { border-color:#11998e; background:#ecfdf5; }
  .xo-opt.on b { color:#0f766e; }
  .xo-opt.warn.on { border-color:#d97706; background:#fffbeb; }
  .xo-opt.warn.on b { color:#92400e; }
  .xo-opt[disabled] { cursor:not-allowed; opacity:.55; }
  .xo-note { margin-top:8px; font-size:12px; line-height:1.5; color:#92400e; background:#fffbeb;
             border:1px solid #fde68a; border-radius:8px; padding:8px 10px; text-align:left; }
`;

export const askXmlExportOptions = async () => {
  const state = { facility: 'hospital', labs: 'include' };

  const result = await Swal.fire({
    title: 'Export XML For…',
    width: 560,
    html: `
      <style>${optionCss}</style>
      <div class="xo-group">
        <div class="xo-legend"><span>1 · Facility type</span></div>
        <div class="xo-row">
          <button type="button" class="xo-opt" data-facility="clinic">
            <b>Clinic</b><small>Investigations are never sent for a clinic.</small>
          </button>
          <button type="button" class="xo-opt" data-facility="hospital">
            <b>Hospital</b><small>You choose below whether labs go in.</small>
          </button>
        </div>
      </div>
      <div class="xo-group">
        <div class="xo-legend"><span>2 · Laboratory investigations</span><span id="xo-labs-aside"></span></div>
        <div class="xo-row">
          <button type="button" class="xo-opt" data-labs="include">
            <b>Include labs</b><small>The file is built exactly as it always has been.</small>
          </button>
          <button type="button" class="xo-opt warn" data-labs="exclude">
            <b>Exclude labs</b><small>Every investigation is left out. Nothing else changes.</small>
          </button>
        </div>
        <div class="xo-note" id="xo-labs-note"></div>
      </div>`,
    showCancelButton: true,
    confirmButtonText: 'Generate XML',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#11998e',
    cancelButtonColor: '#64748b',
    reverseButtons: true,
    focusConfirm: false,
    didOpen: (popup) => {
      const paint = () => {
        const clinic = state.facility === 'clinic';
        const labs = clinic ? 'exclude' : state.labs;
        popup.querySelectorAll('[data-facility]').forEach((b) => {
          b.classList.toggle('on', b.dataset.facility === state.facility);
        });
        popup.querySelectorAll('[data-labs]').forEach((b) => {
          b.classList.toggle('on', b.dataset.labs === labs);
          b.disabled = clinic;
        });
        popup.querySelector('#xo-labs-aside').textContent = clinic
          ? 'Set by the facility type'
          : labs === 'include' ? 'Included, as normal' : 'Left out of the file';
        const note = popup.querySelector('#xo-labs-note');
        note.style.display = labs === 'exclude' ? 'block' : 'none';
        note.innerHTML = clinic
          ? 'A clinic submission never carries investigations. Change the facility type if that is wrong.'
          : 'The claims keep their investigations — this only leaves them out of this file. '
            + 'The download will be named <strong>_nolabs</strong>.';
      };
      popup.querySelectorAll('[data-facility]').forEach((b) => b.addEventListener('click', () => {
        state.facility = b.dataset.facility; paint();
      }));
      popup.querySelectorAll('[data-labs]').forEach((b) => b.addEventListener('click', () => {
        if (!b.disabled) { state.labs = b.dataset.labs; paint(); }
      }));
      paint();
    },
  });

  if (!result.isConfirmed) return null;
  return {
    facilityType: state.facility,
    includeInvestigations: state.facility === 'hospital' && state.labs === 'include',
  };
};
